import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './Modal.css';

interface EditDealerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  dealerId: string;
}

interface DealerFormData {
  dealer_name: string;
  dealer_id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  district_manager: string;
  regional_manager: string;
  email?: string;
  phone?: string;
}

interface DistrictManager {
  manager_name: string;
  regional_manager: string;
}

const EditDealerModal: React.FC<EditDealerModalProps> = ({ isOpen, onClose, onSuccess, dealerId }) => {
  const [formData, setFormData] = useState<DealerFormData>({
    dealer_name: '',
    dealer_id: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    district_manager: '',
    regional_manager: '',
    email: '',
    phone: '',
  });

  const [originalData, setOriginalData] = useState<DealerFormData | null>(null);
  const [districtManagers, setDistrictManagers] = useState<DistrictManager[]>([]);
  const [regionalManagers, setRegionalManagers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geocodePreview, setGeocodePreview] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      fetchDealerAndManagers();
    }
  }, [isOpen, dealerId]);

  const fetchDealerAndManagers = async () => {
    setLoading(true);
    try {
      console.log('[EditDealerModal] Fetching dealer with ID:', dealerId);

      // Fetch dealer details and managers in parallel
      const [dealerResponse, districtResponse, regionalResponse] = await Promise.all([
        adminAPI.getDealer(dealerId),
        adminAPI.listManagers({ role: 'district', per_page: 1000 }),
        adminAPI.listManagers({ role: 'regional', per_page: 1000 }),
      ]);

      console.log('[EditDealerModal] Dealer API Response:', dealerResponse);
      console.log('[EditDealerModal] dealerResponse.data:', dealerResponse.data);
      console.log('[EditDealerModal] dealerResponse.success:', dealerResponse.success);
      console.log('[EditDealerModal] dealerResponse.dealer:', dealerResponse.dealer);

      // Log district managers response
      console.log('[EditDealerModal] District Managers Response:', districtResponse);
      console.log('[EditDealerModal] Regional Managers Response:', regionalResponse);

      // Access data from Axios response wrapper
      let responseData = dealerResponse.data || dealerResponse;

      // If response is a string, parse it
      if (typeof responseData === 'string') {
        console.log('[EditDealerModal] Response is string, parsing JSON...');
        responseData = JSON.parse(responseData);
      }

      console.log('[EditDealerModal] Response data:', responseData);
      console.log('[EditDealerModal] Response data type:', typeof responseData);
      console.log('[EditDealerModal] Response data.success:', responseData.success);
      console.log('[EditDealerModal] Response data.dealer:', responseData.dealer);

      if (responseData.success && responseData.dealer) {
        const dealer = responseData.dealer;
        console.log('[EditDealerModal] Dealer data:', dealer);

        const initialData: DealerFormData = {
          dealer_name: dealer.dealer_name || '',
          dealer_id: dealer.dealer_id || '',
          address: dealer.address || '',
          city: dealer.city || '',
          state: dealer.state || '',
          zip: dealer.zip || '',
          district_manager: dealer.district_manager || '',
          regional_manager: dealer.regional_manager || '',
          email: dealer.email || '',
          phone: dealer.phone || '',
        };
        console.log('[EditDealerModal] Initial form data:', initialData);

        setFormData(initialData);
        setOriginalData(initialData);
        updateGeocodePreview(initialData);
        console.log('[EditDealerModal] Form data set successfully');
      } else {
        console.error('[EditDealerModal] Response validation failed:', {
          success: responseData.success,
          hasDealer: !!responseData.dealer
        });
      }

      // Access data from Axios response wrapper for managers too
      const districtData = districtResponse.data || districtResponse;
      const regionalData = regionalResponse.data || regionalResponse;

      console.log('[EditDealerModal] District data parsed:', districtData);
      console.log('[EditDealerModal] Regional data parsed:', regionalData);

      if (districtData.success) {
        console.log('[EditDealerModal] District managers raw count:', districtData.managers?.length);
        const districts = districtData.managers
          .filter((m: any) => m.manager_name && m.manager_name.trim())
          .map((m: any) => ({
            manager_name: m.manager_name,
            regional_manager: m.reports_to || '',
          }));
        console.log('[EditDealerModal] District managers after filter:', districts.length);

        // Check for Travis specifically
        const travis = districts.find((d: any) => d.manager_name && d.manager_name.toUpperCase().includes('TURNBULL'));
        console.log('[EditDealerModal] Travis Turnbull found:', travis ? 'YES - ' + travis.manager_name : 'NO');

        // Log first 10 for debugging
        console.log('[EditDealerModal] First 10 district managers:', districts.slice(0, 10).map((d: any) => d.manager_name));

        setDistrictManagers(districts);

        // Check if dealer's district_manager matches any option
        if (responseData.dealer && responseData.dealer.district_manager) {
          const dealerDM = responseData.dealer.district_manager;
          const matchFound = districts.find((d: any) => d.manager_name === dealerDM);
          console.log('[EditDealerModal] Dealer district_manager:', dealerDM);
          console.log('[EditDealerModal] Match found in dropdown options:', matchFound ? 'YES' : 'NO');

          if (!matchFound) {
            console.warn('[EditDealerModal] Dealer district_manager does NOT match any dropdown option!');
            // Find close matches
            const similar = districts.filter((d: any) =>
              d.manager_name && dealerDM &&
              (d.manager_name.includes(dealerDM.substring(0, 10)) || dealerDM.includes(d.manager_name.substring(0, 10)))
            );
            if (similar.length > 0) {
              console.log('[EditDealerModal] Similar options found:', similar.map((d: any) => d.manager_name));
            }
          }
        }
      } else {
        console.error('[EditDealerModal] District data.success is false:', districtData);
      }

      if (regionalData.success) {
        console.log('[EditDealerModal] Regional managers raw count:', regionalData.managers?.length);
        const regionals = regionalData.managers
          .filter((m: any) => m.manager_name && m.manager_name.trim())
          .map((m: any) => m.manager_name);
        console.log('[EditDealerModal] Regional managers after filter:', regionals.length);
        setRegionalManagers(regionals);
      } else {
        console.error('[EditDealerModal] Regional data.success is false:', regionalData);
      }
    } catch (err) {
      console.error('Error fetching dealer and managers:', err);
      setErrors({ submit: 'Failed to load dealer data' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    // Auto-populate regional manager when district manager is selected
    if (name === 'district_manager') {
      const district = districtManagers.find((d) => d.manager_name === value);
      if (district && district.regional_manager) {
        setFormData((prev) => ({ ...prev, regional_manager: district.regional_manager }));
      }
    }

    // Update geocode preview when address changes
    if (['address', 'city', 'state', 'zip'].includes(name)) {
      updateGeocodePreview({ ...formData, [name]: value });
    }
  };

  const updateGeocodePreview = (data: DealerFormData) => {
    const parts = [data.address, data.city, data.state, data.zip].filter(Boolean);
    setGeocodePreview(parts.join(', '));
  };

  const addressChanged = (): boolean => {
    if (!originalData) return false;
    return (
      formData.address !== originalData.address ||
      formData.city !== originalData.city ||
      formData.state !== originalData.state ||
      formData.zip !== originalData.zip
    );
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.dealer_name.trim()) {
      newErrors.dealer_name = 'Dealer name is required';
    }

    if (!formData.dealer_id.trim()) {
      newErrors.dealer_id = 'Dealer ID is required';
    } else if (!/^\d{8}$/.test(formData.dealer_id)) {
      newErrors.dealer_id = 'Dealer ID must be exactly 8 digits';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    } else if (!/^[A-Z]{2}$/.test(formData.state.toUpperCase())) {
      newErrors.state = 'State must be 2-letter code (e.g., CO)';
    }

    if (!formData.zip.trim()) {
      newErrors.zip = 'ZIP code is required';
    } else if (!/^\d{5}$/.test(formData.zip)) {
      newErrors.zip = 'ZIP code must be exactly 5 digits';
    }

    if (!formData.district_manager) {
      newErrors.district_manager = 'District manager is required';
    }

    if (!formData.regional_manager) {
      newErrors.regional_manager = 'Regional manager is required';
    }

    // Optional email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData: any = {
        ...formData,
        state: formData.state.toUpperCase(),
      };

      // Remove empty optional fields
      if (!submitData.email) delete submitData.email;
      if (!submitData.phone) delete submitData.phone;

      // If address changed, trigger re-geocoding
      if (addressChanged()) {
        submitData.force_geocode = true;
      }

      const response = await adminAPI.updateDealer(dealerId, submitData);

      if (response.success) {
        alert('Dealer updated successfully!');
        onSuccess();
        handleClose();
      } else {
        setErrors({ submit: response.error || 'Failed to update dealer' });
      }
    } catch (err: any) {
      console.error('Error updating dealer:', err);
      setErrors({
        submit: err.response?.data?.error || err.message || 'Failed to update dealer',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      dealer_name: '',
      dealer_id: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      district_manager: '',
      regional_manager: '',
      email: '',
      phone: '',
    });
    setOriginalData(null);
    setErrors({});
    setGeocodePreview('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="material-icons">edit</span>
            Edit Dealer
          </h2>
          <button className="modal-close" onClick={handleClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        {loading ? (
          <div className="modal-body">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="loading-spinner"></div>
              <p>Loading dealer details...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="modal-body">
              {errors.submit && (
                <div className="form-error-banner">
                  <span className="material-icons">error</span>
                  {errors.submit}
                </div>
              )}

              {addressChanged() && (
                <div className="form-success-banner">
                  <span className="material-icons">info</span>
                  <div>
                    <strong>Address Changed:</strong> The dealer location will be re-geocoded and radius ZIPs will be recalculated.
                  </div>
                </div>
              )}

              {/* Dealer Information */}
              <div className="form-section">
                <h3>Dealer Information</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="dealer_name">
                      Dealer Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="dealer_name"
                      name="dealer_name"
                      value={formData.dealer_name}
                      onChange={handleInputChange}
                      className={errors.dealer_name ? 'error' : ''}
                      placeholder="e.g., Smith Equipment LLC"
                    />
                    {errors.dealer_name && <span className="field-error">{errors.dealer_name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="dealer_id">
                      Dealer ID <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="dealer_id"
                      name="dealer_id"
                      value={formData.dealer_id}
                      onChange={handleInputChange}
                      className={errors.dealer_id ? 'error' : ''}
                      placeholder="8-digit ID (e.g., 78925900)"
                      maxLength={8}
                      disabled
                    />
                    {errors.dealer_id && <span className="field-error">{errors.dealer_id}</span>}
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="form-section">
                <h3>Location Information</h3>

                <div className="form-group">
                  <label htmlFor="address">
                    Street Address <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={errors.address ? 'error' : ''}
                    placeholder="e.g., 123 Main Street"
                  />
                  {errors.address && <span className="field-error">{errors.address}</span>}
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label htmlFor="city">
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={errors.city ? 'error' : ''}
                      placeholder="e.g., Denver"
                    />
                    {errors.city && <span className="field-error">{errors.city}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">
                      State <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={errors.state ? 'error' : ''}
                      placeholder="CO"
                      maxLength={2}
                      style={{ textTransform: 'uppercase' }}
                    />
                    {errors.state && <span className="field-error">{errors.state}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="zip">
                      ZIP Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      className={errors.zip ? 'error' : ''}
                      placeholder="80202"
                      maxLength={5}
                    />
                    {errors.zip && <span className="field-error">{errors.zip}</span>}
                  </div>
                </div>

                {geocodePreview && (
                  <div className="geocode-preview">
                    <span className="material-icons">location_on</span>
                    <div>
                      <strong>Address Preview:</strong>
                      <p>{geocodePreview}</p>
                      {addressChanged() && (
                        <small>This address will be re-geocoded upon save</small>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Hierarchy Assignment */}
              <div className="form-section">
                <h3>Management Hierarchy</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="district_manager">
                      District Manager <span className="required">*</span>
                    </label>
                    <select
                      id="district_manager"
                      name="district_manager"
                      value={formData.district_manager}
                      onChange={handleInputChange}
                      className={errors.district_manager ? 'error' : ''}
                    >
                      <option value="">Select District Manager</option>
                      {districtManagers.map((dm) => (
                        <option key={dm.manager_name} value={dm.manager_name}>
                          {dm.manager_name}
                        </option>
                      ))}
                    </select>
                    {errors.district_manager && (
                      <span className="field-error">{errors.district_manager}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="regional_manager">
                      Regional Manager <span className="required">*</span>
                    </label>
                    <select
                      id="regional_manager"
                      name="regional_manager"
                      value={formData.regional_manager}
                      onChange={handleInputChange}
                      className={errors.regional_manager ? 'error' : ''}
                    >
                      <option value="">Select Regional Manager</option>
                      {regionalManagers.map((rm) => (
                        <option key={rm} value={rm}>
                          {rm}
                        </option>
                      ))}
                    </select>
                    {errors.regional_manager && (
                      <span className="field-error">{errors.regional_manager}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information (Optional) */}
              <div className="form-section">
                <h3>Contact Information (Optional)</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? 'error' : ''}
                      placeholder="dealer@example.com"
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="555-1234"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting || loading}>
                {submitting ? (
                  <>
                    <span className="button-spinner"></span>
                    Updating Dealer...
                  </>
                ) : (
                  <>
                    <span className="material-icons">save</span>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditDealerModal;
